import { Tooltip } from '@/design-system';
import ThumbsDownIcon from '@/icons/ThumbsDownIcon.svg?react';
import ThumbsUpIcon from '@/icons/ThumbsUpIcon.svg?react';
import { FeedbackDelta } from '@/types/schema';
import { getPrettyFeedbackName } from '@/utils/get-pretty-feedback-name';
import { cn } from '@/utils/tailwind';

import ArrowDownIcon from './icons/ArrowDownIcon.svg?react';
import ArrowUpIcon from './icons/ArrowUpIcon.svg?react';

export const FeedbackDeltaComponent = (props: {
  delta: FeedbackDelta;
  isFiltering: boolean;
  isReversed?: boolean;
  isFilteringImprovements: boolean;
  isComparativeView: boolean;
  filterToImprovements: (improvements: boolean) => void;
  expandedView?: boolean;
  feedbackKey?: string;
  otherExperimentName?: string;
}) => {
  const improvedExamples = props.isReversed
    ? props.delta.regressed_examples
    : props.delta.improved_examples;
  const regressedExamples = props.isReversed
    ? props.delta.improved_examples
    : props.delta.regressed_examples;
  const hasImprovements = improvedExamples.length > 0;
  const hasRegressions = regressedExamples.length > 0;

  const improvementsTooltipText = props.isComparativeView
    ? `${improvedExamples.length} ${
        improvedExamples.length !== 1 ? 'runs' : 'run'
      } scored higher on ${getPrettyFeedbackName(props.feedbackKey ?? '')} vs ${
        props.otherExperimentName
      }`
    : `${improvedExamples.length} ${
        improvedExamples.length === 1 ? 'Improvement' : 'Improvements'
      } vs. Baseline`;
  const regressionsTooltipText = props.isComparativeView
    ? `${regressedExamples.length} ${
        regressedExamples.length !== 1 ? 'runs' : 'run'
      } scored lower on ${getPrettyFeedbackName(props.feedbackKey ?? '')} vs ${
        props.otherExperimentName
      }`
    : `${regressedExamples.length} ${
        regressedExamples.length === 1 ? 'Regression' : 'Regressions'
      } vs. Baseline`;

  return (
    <div className="flex shrink-0 gap-1">
      <Tooltip side="top" title={improvementsTooltipText}>
        <button
          type="button"
          disabled={
            !hasImprovements &&
            !(props.isFiltering && props.isFilteringImprovements)
          }
          className={cn(
            'flex flex-shrink-0 items-center justify-center gap-1 rounded-full border border-secondary py-1 pl-2.5 pr-1.5 text-sm font-medium text-tertiary opacity-40 transition-all data-[state=open]:bg-tertiary',
            hasImprovements && 'cursor-pointer border-ls-gray-100 opacity-100',
            hasImprovements &&
              !(props.isFiltering && props.isFilteringImprovements) &&
              'bg-white hover:bg-secondary-hover active:bg-secondary dark:bg-black dark:hover:bg-secondary-hover dark:active:bg-secondary',
            props.isFiltering &&
              props.isFilteringImprovements &&
              'cursor-pointer border-success bg-success-secondary opacity-100 hover:bg-success-secondary active:bg-success-secondary'
          )}
          onClick={() => {
            if (
              hasImprovements ||
              (props.isFiltering && props.isFilteringImprovements)
            ) {
              props.filterToImprovements(true);
            }
          }}
        >
          <div className={cn(hasImprovements && 'text-success')}>
            {improvedExamples.length}
          </div>
          {props.isComparativeView ? (
            <ThumbsUpIcon
              className={cn(
                'mr-0.5 h-4 w-4',
                hasImprovements && 'text-success'
              )}
            />
          ) : (
            <ArrowUpIcon
              className={cn('h-4 w-4', hasImprovements && 'text-success')}
            />
          )}
        </button>
      </Tooltip>
      <Tooltip side="top" title={regressionsTooltipText}>
        <button
          type="button"
          disabled={
            !hasRegressions &&
            !(props.isFiltering && !props.isFilteringImprovements)
          }
          className={cn(
            'flex flex-shrink-0 items-center justify-center gap-1 rounded-full border border-secondary py-1 pl-2.5 pr-1.5 text-sm font-medium text-tertiary opacity-40 transition-all data-[state=open]:bg-tertiary',
            hasRegressions && 'cursor-pointer border-ls-gray-100 opacity-100',
            hasRegressions &&
              !(props.isFiltering && !props.isFilteringImprovements) &&
              'bg-white hover:bg-secondary-hover active:bg-secondary dark:bg-black dark:hover:bg-secondary-hover dark:active:bg-secondary',
            props.isFiltering &&
              !props.isFilteringImprovements &&
              'cursor-pointer border-error bg-error-secondary opacity-100 hover:bg-error-secondary active:bg-error-secondary'
          )}
          onClick={() => {
            if (
              hasRegressions ||
              (props.isFiltering && !props.isFilteringImprovements)
            ) {
              props.filterToImprovements(false);
            }
          }}
        >
          <div className={cn(hasRegressions && 'text-error')}>
            {regressedExamples.length}
          </div>
          {props.isComparativeView ? (
            <ThumbsDownIcon
              className={cn('mr-0.5 h-4 w-4', hasRegressions && 'text-error')}
            />
          ) : (
            <ArrowDownIcon
              className={cn('h-4 w-4', hasRegressions && 'text-error')}
            />
          )}
        </button>
      </Tooltip>
    </div>
  );
};
